import React from "react";

const CLASSIFICATION_LABELS = {
    strong_success: "Strong Success",
    moderate_success: "Moderate Success",
    weak_success: "Weak Success",
    neutral: "Neutral",
    weak_failure: "Weak Failure",
    moderate_failure: "Moderate Failure",
    severe_failure: "Severe Failure",
};

const SEVERITY_COLORS = {
    positive: "#4ade80",
    neutral: "#9feae8",
    negative: "#f87171",
};

const RELIABILITY_COLORS = {
    high: "#4ade80",
    medium: "#60a5fa",
    low: "#fbbf24",
};

function scorePct(value) {
    if (typeof value !== "number" || !Number.isFinite(value)) return null;
    return `${Math.round(value * 100)}%`;
}

function compactList(values, limit = 2) {
    return Array.isArray(values) ? values.filter(Boolean).slice(0, limit) : [];
}

function labelForClassification(classification) {
    return CLASSIFICATION_LABELS[classification] || classification || "Not available";
}

function titleStyle(compact) {
    return {
        fontSize: compact ? "10px" : "11px",
        color: "var(--savvy-muted)",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        marginBottom: compact ? 2 : 3,
    };
}

export default function OutcomeConfidencePanel({
    outcomeClassification,
    evidenceConfidence,
    compact = false,
    showUnavailable = false,
}) {
    const reasons = [
        ...compactList(outcomeClassification?.reasons, compact ? 1 : 2),
        ...compactList(evidenceConfidence?.reasons, compact ? 1 : 2),
    ].slice(0, compact ? 2 : 3);
    const missingEvidence = compactList(evidenceConfidence?.missing_evidence, compact ? 1 : 2);

    if (!outcomeClassification && !evidenceConfidence) {
        if (!showUnavailable) return null;
        return (
            <div style={{ fontSize: compact ? "10px" : "11px", color: "var(--savvy-dim)" }}>
                Outcome evidence not available
            </div>
        );
    }

    const outcomeSeverityColor = SEVERITY_COLORS[outcomeClassification?.severity] || "var(--savvy-text)";
    const reliabilityColor = RELIABILITY_COLORS[evidenceConfidence?.confidence] || "var(--savvy-text)";

    return (
        <div style={{ display: "grid", gap: compact ? 4 : 6 }}>
            <div>
                <div style={titleStyle(compact)}>Outcome</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                    <span style={{ fontSize: compact ? "11px" : "12px", fontWeight: 700, color: outcomeSeverityColor }}>
                        {labelForClassification(outcomeClassification?.classification)}
                    </span>
                    {outcomeClassification?.confidence && (
                        <span style={{ fontSize: compact ? "10px" : "11px", color: "var(--savvy-dim)" }}>
                            {outcomeClassification.confidence} classification confidence
                        </span>
                    )}
                    {!outcomeClassification && showUnavailable && (
                        <span style={{ fontSize: compact ? "10px" : "11px", color: "var(--savvy-dim)" }}>
                            Not available
                        </span>
                    )}
                </div>
            </div>

            <div>
                <div style={titleStyle(compact)}>Evidence Reliability</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                    <span style={{ fontSize: compact ? "11px" : "12px", fontWeight: 700, color: reliabilityColor }}>
                        {evidenceConfidence?.confidence ? `${evidenceConfidence.confidence} reliability` : "Not available"}
                    </span>
                    {scorePct(evidenceConfidence?.score) && (
                        <span style={{ fontSize: compact ? "10px" : "11px", color: "var(--savvy-dim)" }}>
                            score {scorePct(evidenceConfidence.score)}
                        </span>
                    )}
                    {scorePct(evidenceConfidence?.sample_size_score) && (
                        <span style={{ fontSize: compact ? "10px" : "11px", color: "var(--savvy-dim)" }}>
                            sample {scorePct(evidenceConfidence.sample_size_score)}
                        </span>
                    )}
                    {scorePct(evidenceConfidence?.coverage_score) && (
                        <span style={{ fontSize: compact ? "10px" : "11px", color: "var(--savvy-dim)" }}>
                            coverage {scorePct(evidenceConfidence.coverage_score)}
                        </span>
                    )}
                    {scorePct(evidenceConfidence?.consistency_score) && (
                        <span style={{ fontSize: compact ? "10px" : "11px", color: "var(--savvy-dim)" }}>
                            consistency {scorePct(evidenceConfidence.consistency_score)}
                        </span>
                    )}
                </div>
            </div>

            {reasons.length > 0 && (
                <div style={{ display: "grid", gap: 2 }}>
                    {reasons.map((reason) => (
                        <div key={reason} style={{ fontSize: compact ? "10px" : "11px", color: "var(--savvy-muted)" }}>
                            {reason}
                        </div>
                    ))}
                </div>
            )}

            {missingEvidence.length > 0 && (
                <div style={{ fontSize: compact ? "10px" : "11px", color: "var(--savvy-dim)" }}>
                    Missing evidence: {missingEvidence.join(", ")}
                </div>
            )}
        </div>
    );
}
